import React, { FC } from "react";
import styled, { css } from "styled-components";
import { Button, tombac } from "tombac";
import { TrialBadge } from "../../../components/Trial";
import { getProductIcon } from "../../../icons/getProductIcon";
import {
  AccessType,
  UserProduct,
  UserProductFallback,
} from "../../legolandApi";
import { TrackingFunction } from "../LegolandNavbarTypes";

export interface ProductTileProps {
  isActive?: boolean;
  product: UserProduct | UserProductFallback;
  track: TrackingFunction;
}

interface ContainerButtonProps {
  isActive: boolean;
  isExpired: boolean;
}

const ContainerButton = styled(Button)<ContainerButtonProps>`
  border-radius: ${tombac.unit(10)};
  height: ${tombac.unit(124)};
  padding: 0;
  position: relative;
  width: ${tombac.unit(124)};
  visibility: inherit;

  ${({ isActive, isExpired }) =>
    isActive || isExpired
      ? css`
          background-color: ${tombac.alpha("accent", 8)};
          border: ${tombac.unit(1)} solid ${tombac.color("accent", 500)};
          color: ${tombac.color("accent", 500)};

          &:not([disabled]):hover {
            background-color: ${tombac.alpha("accent", 16)};
          }
        `
      : null}

  ${({ isExpired }) =>
    isExpired
      ? css`
          filter: grayscale(100%);
          opacity: 0.2;
        `
      : undefined}

  & > span {
    align-items: center;
    display: flex;
    flex-flow: column;
    justify-content: center;
    text-align: center;
    ${tombac.text({ fontSize: 12, fontWeight: 500 })}
  }
`;

const ExpiredLabel = styled.span`
  background-color: ${tombac.color.black};
  bottom: 0;
  display: none;
  padding-top: 0.2em; /* Noway font alignment */
  position: absolute;
  width: 100%;

  ${ContainerButton}:hover > & {
    display: block;
  }
`;

const Icon = styled.span`
  display: block;
  height: ${tombac.unit(57)};
  margin: 0 auto ${tombac.space(1)};
  width: ${tombac.unit(57)};
`;

const Name = styled.span`
  white-space: normal;
  ${tombac.text({ color: "inherit", fontSize: 12, fontWeight: 500 })}
`;

const StyledTrialBadge = styled(TrialBadge)`
  bottom: ${tombac.space(0.5)};
  height: ${tombac.space(2)};
  margin: 0;
  position: absolute;
`;

export const ProductTile: FC<ProductTileProps> = ({
  isActive = false,
  product,
  track,
}) => {
  if (product.access.status === "DENIED") return null;

  const isExpired = product.access.status === "EXPIRED";
  const isTrial =
    !isExpired &&
    !product.isFallback &&
    product.access.accessType === AccessType.Evaluation;

  return (
    <ContainerButton
      disabled={isExpired}
      forwardedAs={isExpired ? "button" : "a"}
      href={!isExpired ? product.url : undefined}
      isActive={isActive}
      isExpired={isExpired}
      onClick={() => !isExpired && track("launch", product.url)}
      variant="flat"
    >
      <Icon as={getProductIcon(product.id)} />{" "}
      <Name>{product.displayName}</Name>{" "}
      {isExpired && <ExpiredLabel>Expired</ExpiredLabel>}
      {isTrial && <StyledTrialBadge />}
    </ContainerButton>
  );
};
